import { clamp, defineElement } from '@utils/gw'
import { DetailsDisclosureBase } from 'details-disclosure'
import { GWElement } from 'element'

const NAV_TRANSITION_OFFSET = 0.05
const NAV_TRANSITION_BASE = 0.5
const NAV_TRANSITION_EASE = 'power2'

class MegaMenu extends GWElement {
  menus = []
  _actionMenu = null
  _action = undefined
  previousAction = undefined
  _activeMenu = null
  previousMenu = null
  // open = false
  bgTween = false
  menuTimeline = false
  focusTrap = null
  tweenVars = {
    offset: NAV_TRANSITION_OFFSET,
    duration: {
      base: NAV_TRANSITION_BASE,
      reduced: NAV_TRANSITION_BASE * 0.7,
      delay: NAV_TRANSITION_BASE * 0.3 + NAV_TRANSITION_OFFSET,
    },
  }

  constructor() {
    super()

    // TODO: Creat helper for this
    this.menus = [...this.querySelectorAll('menu-disclosure')]
    this.rootLinks = [...this.querySelectorAll('.header__menu-item--link, .header__link, .header__icon')]
    this.header = document.querySelector('.section-header .header')
    this.headerWrapper = document.querySelector('.header-wrapper')
    this.background = this.header.querySelector('[data-menu-background]')
    this.disclosureContainer = this.closest('[data-disclosure-container]')

    // TODO: Update element base to move event listener definition elsewhere (bindEvents?)
    this.addListener(this, 'mouseleave', this.onMouseLeave)
    this.addListener(this, 'keydown', this.onKeyDown)
  }

  set activeMenu(menu) {
    this.previousMenu = this.activeMenu
    this._activeMenu = menu
  }

  get activeMenu() {
    return this._activeMenu
  }

  init() {
    super.init()
    this.bindRootLinks()
  }

  bindRootLinks() {
    if (typeof window.hoverintent !== 'undefined') {
      this.rootLinkListeners = []
      this.rootLinks.forEach(link => {
        const rootLinkListener = hoverintent(link, () => {
          this.onRootLinkHover();
        }, () => { })
        rootLinkListener.options({ interval: 50 })
        this.rootLinkListeners.push(rootLinkListener)
      })
    } else {
      this.rootLinks.forEach(link => link.addEventListener('mouseenter', () => {
        this.onRootLinkHover()
      }))
    }
  }

  unbindRootLinks() {
    if (this.rootLinkListeners) {
      this.rootLinkListeners.forEach(listener => listener.remove())
      this.rootLinkListeners = null
    } else {
      this.rootLinks.forEach(link => link.removeEventListener('mouseenter', this.onRootLinkHover.bind(this)))
    }
  }

  onRootLinkHover() {
    this.close()
  }

  onMouseLeave() {
    this.cancelClose()

    this.closingTime = setTimeout(() => {
      this.close()
    }, 150)
  }

  onKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault()
      this.close()
      this.activeMenu?.summaryElement?.focus()
    }
  }

  animateBackground(activeMenu = this.activeMenu) {
    const action = this.action

    this.bgTween = gsap.to(this.background, {
      onComplete: () => {
        this.bgTween = undefined

        if (action === 'close') {
          this.disclosureContainer.classList.remove('has-menu-buffer')
        }
      },
      y: action === 'close' ? 0 : activeMenu.getSubnavHeight.bind(activeMenu),
      duration: action === 'switch' ? this.tweenVars.duration.reduced : this.tweenVars.duration.base,
      ease: `${NAV_TRANSITION_EASE}.inOut`,
      overwrite: true,
      immediateRender: true,
      delay: action === 'close' ? this.tweenVars.offset : 0,
    })
  }

  get isOpen() {
    return this.menus.some(menu => menu.isOpen()) || this.disclosureContainer.classList.contains('has-menu-buffer')
  }

  get animating() {
    return this.bgTween || this.menuTimeline
  }

  open(activeMenu) {
    this.cancelClose()
    this.activeMenu = activeMenu

    this.animateBackground(activeMenu)
  }

  get action() {
    let action
    if (!this.activeMenu) {
      action = 'close'
    } else if (this.activeMenu && this.previousMenu && this.previousMenu !== this.activeMenu) {
      action = 'switch'
    } else {
      action = 'open'
    }

    const actionMenu = action === 'close' ? this.previousMenu : this.activeMenu

    if (action === this._action && this._actionMenu === actionMenu) return this._action

    this.previousAction = this._action
    this._action = action
    this._actionMenu = actionMenu

    return this._action
  }

  close(closeChildren = true) {
    this.cancelClose()

    if (!this.isOpen) return

    this.headerWrapper.preventHide = false
    this.activeMenu = null

    if (closeChildren) {
      this.menus.forEach(menu => menu.close())
    }

    this.animateBackground()
  }

  cancelClose() {
    clearTimeout(this.closingTime)
    this.closingTime = undefined
  }
}

defineElement('mega-menu', MegaMenu)

// TODO: Remove disclosureContainer from this class (& HTML)
class MenuDisclosure extends DetailsDisclosureBase {
  constructor() {
    super()

    this.parent = this.closest('mega-menu')
    this.item = this.closest('li')
    this.child = this.querySelector('summary + div')
    this.innerChild = this.child.firstElementChild
    // TODO: Remove this and create getter that returns filtered parent menus (not this menu)
    this.siblings = [...this.disclosureContainer.querySelectorAll('menu-disclosure')].filter(
      sibling => sibling !== this
    )
    this.headerWrapper = document.querySelector('.header-wrapper')
    this.onMouseOver = this.handleMouseOver.bind(this)
    this.onResize = this.handleResize.bind(this)
    this.onKeyDown = this.handleKeyDown.bind(this)
    this.hoverIntentListeners = null
    this.focusableElements = null
    this.firstFocusableElement = null
    this.lastFocusableElement = null
  }

  bindEvents() {
    super.bindEvents()

    if (typeof window.hoverintent !== 'undefined') {
      this.hoverintentListener = hoverintent(this, this.onMouseOver, () => { })

      this.hoverintentListener.options({ interval: 50 })
    } else {
      this.addEventListener('mouseover', this.onMouseOver)
    }

    this.summaryElement.addEventListener('keydown', this.onKeyDown)
  }

  unbindEvents() {
    super.unbindEvents()

    if (this.hoverintentListener) {
      this.hoverintentListener.remove()
      this.hoverintentListener = undefined
    } else {
      this.removeEventListener('mouseover', this.onMouseOver)
    }

    this.summaryElement.removeEventListener('keydown', this.onKeyDown)
  }

  handleSummaryClick(e) {
    const innerLink = e.currentTarget.querySelector('a')
    if (innerLink) return

    e.preventDefault()
  }

  handleKeyDown(e) {
    const isOpen = this.isOpen()

    switch (e.key) {
      case 'Enter':
      case ' ':
        const innerLink = e.currentTarget.querySelector('a')
        if (innerLink && !isOpen) {
          return
        }
        e.preventDefault()
        if (isOpen) {
          this.close()
        } else {
          this.open()
          setTimeout(() => {
            const firstLink = this.child.querySelector('a, button')
            if (firstLink) {
              firstLink.focus()
            }
          }, 100)
        }
        break
      case 'Escape':
        if (isOpen) {
          e.preventDefault()
          e.stopPropagation()
          this.close()
          this.summaryElement.focus()
        }
        break
      case 'ArrowDown':
        if (!isOpen) {
          e.preventDefault()
          this.open()
          setTimeout(() => {
            const firstLink = this.child.querySelector('a, button')
            if (firstLink) {
              firstLink.focus()
            }
          }, 100)
        }
        break
      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault()
          this.close()
        }
        break
      case 'ArrowLeft':
        e.preventDefault()
        this.focusPreviousSibling()
        break
      case 'ArrowRight':
        e.preventDefault()
        this.focusNextSibling()
        break
      case 'Home':
        e.preventDefault()
        this.focusFirstSibling()
        break
      case 'End':
        e.preventDefault()
        this.focusLastSibling()
        break
    }
  }

  focusNextSibling() {
    const allMenus = [...this.parent.menus]
    const currentIndex = allMenus.indexOf(this)
    const nextMenu = allMenus[currentIndex + 1] || allMenus[0]
    nextMenu.summaryElement.focus()
  }

  focusPreviousSibling() {
    const allMenus = [...this.parent.menus]
    const currentIndex = allMenus.indexOf(this)
    const prevMenu = allMenus[currentIndex - 1] || allMenus[allMenus.length - 1]
    prevMenu.summaryElement.focus()
  }

  focusFirstSibling() {
    const allMenus = [...this.parent.menus]
    if (allMenus[0]) {
      allMenus[0].summaryElement.focus()
    }
  }

  focusLastSibling() {
    const allMenus = [...this.parent.menus]
    if (allMenus[allMenus.length - 1]) {
      allMenus[allMenus.length - 1].summaryElement.focus()
    }
  }

  setupFocusTrap() {
    this.focusableElements = [
      ...this.child.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ]

    if (this.focusableElements.length === 0) return

    this.firstFocusableElement = this.focusableElements[0]
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1]

    this.child.addEventListener('keydown', this.trapFocus.bind(this))
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault()
        this.lastFocusableElement.focus()
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault()
        this.summaryElement.focus()
      }
    }
  }

  removeFocusTrap() {
    if (this.child) {
      this.child.removeEventListener('keydown', this.trapFocus)
    }
    this.focusableElements = null
    this.firstFocusableElement = null
    this.lastFocusableElement = null
  }

  handleResize() {
    if (this.isOpen()) {
      this.updateHeight()
    }
  }

  getSubnavHeight() {
    const max = this.child.offsetHeight

    this.subnavHeight = max
    return max
  }

  updateHeight() {
    this.child.style.height = ''
    gsap.set(this.parent.background, {
      y: this.getSubnavHeight.bind(this),
    })
  }

  // TODO: Clean up
  timelineStart(action, timeline) {
    this.parent.menuTimeline = timeline

    switch (action) {
      case 'close':
        this.closing = true
        break
      case 'switch':
        this.disclosureContainer.classList.add('is-switching')
        break
      case 'open':
      default:
        this.disclosureContainer.classList.add('has-menu-buffer')
        break
    }
  }

  timelineComplete(action, timeline) {
    if (!timeline) return

    const isCurrentTimeline = timeline === this.timeline

    this.disclosureContainer.classList.remove('is-switching')

    timeline.kill()

    if (!isCurrentTimeline) return

    gsap.set(this.innerChild, { clearProps: 'y' })

    this.parent.menuTimeline = false

    if (action === 'close') {
      this.subnavHeight = 0
      this.closing = false
      this.detailsElement.removeAttribute('open')
      this.disclosureContainer.classList.remove(...this.containerClasses)
    }
  }

  get activeTimeline() {
    return this.parent.menuTimeline
  }

  get animating() {
    return this.parent.animating === this.timeline
  }

  // TODO: Make use of this
  get currentAction() {
    return this.timeline?.action ?? false
  }

  get tweenVars() {
    return this.parent.tweenVars
  }

  applySiblingCloseAnimation(timeline, { action = this.parent.action } = {}) {
    if (action !== 'switch') return

    const openSibling = this.switchFrom
    if (!openSibling) return

    openSibling.closing = true

    this.switchFrom = undefined

    return timeline.to(
      openSibling.child,
      {
        opacity: 0,
        duration: this.tweenVars.duration.reduced,
        overwrite: true,
        ease: `${NAV_TRANSITION_EASE}.out`,
        onComplete: () => {
          openSibling.closing = false
          openSibling.detailsElement.removeAttribute('open')
        },
      },
      0
    )
  }

  applyShiftAnimation(
    timeline,
    {
      action = this.parent.action,
      interrupting = !!this.parent.menuTimeline,
      previousAction = this.parent.previousAction,
    } = {}
  ) {
    if (action === 'switch' && interrupting) return

    const fromClose = interrupting && previousAction === 'close'
    const method = action === 'close' || fromClose ? 'to' : 'from'
    const duration = action === 'switch' ? this.tweenVars.duration.reduced : this.tweenVars.duration.base
    const ease = `${NAV_TRANSITION_EASE}.inOut`
    const overwrite = action !== 'close'
    const immediateRender = false
    const position = action === 'close' ? 0 : this.tweenVars.offset

    const y = () => {
      if (action === 'switch') return clamp(gsap.getProperty(this.parent.background, 'y') - this.subnavHeight, -60, 60)

      return fromClose ? 0 : -60
    }

    return timeline[method](this.innerChild, { y, duration, ease, overwrite, immediateRender }, position)
  }

  applyFadeAnimation(timeline, { action = this.parent.action } = {}) {
    const ease = action === 'switch' ? `${NAV_TRANSITION_EASE}.in` : `${NAV_TRANSITION_EASE}.inOut`
    const opacity = action === 'close' ? 0 : 1
    const duration = this.tweenVars.duration.reduced
    const overwrite = true
    const immediateRender = true
    let position
    switch (action) {
      case 'close':
        position = 0
        break
      case 'switch':
        position = this.tweenVars.offset
        break
      case 'open':
      default:
        position = this.tweenVars.duration.delay
        break
    }

    return timeline.to(this.child, { opacity, duration, ease, overwrite, immediateRender }, position)
  }

  animate() {
    const action = this.parent.action
    const previousAction = this.parent.previousAction
    const interrupting = !!this.parent.menuTimeline

    this.timeline = gsap.timeline()
    this.timeline.action = action

    this.timeline.eventCallback('onStart', this.timelineStart.bind(this, action, this.timeline))
    this.timeline.eventCallback('onComplete', this.timelineComplete.bind(this, action, this.timeline))

    this.applySiblingCloseAnimation(this.timeline, { action })
    this.applyShiftAnimation(this.timeline, { action, interrupting, previousAction })
    this.applyFadeAnimation(this.timeline, { action })
  }

  isOpen() {
    return this.item.classList.contains('is-open')
  }

  get isSwitching() {
    return this.disclosureContainer.classList.contains('is-switching')
  }

  handleMouseOver(e) {
    const fromElementMenu = e.relatedTarget?.closest('menu-disclosure')
    if (fromElementMenu === this) return

    this.parent.cancelClose()

    if (this.closing && e.target.closest('.mega-menu__content') === this.child) {
      return
    }

    this.open()
  }

  open() {
    if (this.isOpen()) return

    this.switchFrom = this.openSibling

    if (this.switchFrom) {
      this.disclosureContainer.classList.add('is-switching')
    }

    this.parent.cancelClose()
    this.closeSiblings()

    super.open()

    this.item.classList.add('is-open')
    this.summaryElement.classList.add('is-open')
    this.summaryElement.setAttribute('aria-expanded', true)
    this.headerWrapper.preventHide = true

    window.addEventListener('resize', this.onResize)

    setTimeout(() => {
      this.disclosureContainer.classList.add('has-menu-buffer')

      this.parent.open(this)
      this.animate()
      this.setupFocusTrap()
    })
  }

  get openSibling() {
    return this.siblings.find(sibling => sibling.isOpen())
  }

  closeSiblings() {
    this.siblings.forEach(sibling => sibling.close(false))
  }

  close(animate = true) {
    if (!this.isOpen()) return

    this.headerWrapper.preventHide = false
    this.item.classList.remove('is-open')
    this.summaryElement.classList.remove('is-open')
    this.summaryElement.setAttribute('aria-expanded', false)

    window.removeEventListener('resize', this.onResize)

    if (!this.isSwitching) {
      this.parent.close(false)
    }

    if (animate) {
      setTimeout(this.animate.bind(this))
    }

    this.removeFocusTrap()
  }
}

customElements.define('menu-disclosure', MenuDisclosure)
